// HOOKS
import { useContext, useEffect } from "react";
import useColumns from "../../../hooks/other/useColumns.other.hook";
import useMultiSelectStore from "../../../hooks/other/useMultiSelectStore.other.hook";
import useSelectAll from "../../../hooks/other/useSelectAll.other.hook";
// CONTEXTS
import { SelectedOrderedPartsContext } from "../../../contexts/parts/selectedOrderedParts.part.context";
import { OrderedPartsContext } from "../../../contexts/parts/orderedParts.context";
// COMPONENTS
import MultiOptionFilter from "../../../components/tables/customizableTable/features/multiOptionFilter/multiOptionFilter";
import Refresh from "../../../components/refresh/refresh.component";
import OrderedPartsRow from "../../../components/tables/rows/parts/ordered/orderedPartsRow.part.row";
import Spinner from "../../../components/spinner/spinner.component";
import Table from "../../../components/tables/customizableTable/table.customizableTable";
import QueryContainer from "../../../components/tables/query/queryContainer.table.component";
import ResizableHeader from "../../../components/tables/customizableTable/headers/resizable.header";
import OrderedPartsInfoBar from "../../../components/infoBar/parts/orderedParts.parts.infoBar.component";
import OrderedPartsActionBar from "../../../components/actionBar/parts/orderedParts.part.actionBar.component";
import PaginationBar from "../../../components/tables/customizableTable/features/pagination/paginationBar";
// CONSTANTS
import DEFAULT_ORDERED_PARTS_COLUMNS from "../../../constants/parts/defaultOrderedPartsColumns";
import ORDERED_PART_STATUSES from "../../../constants/parts/orderedPartStatuses.part.constant";

const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
  statusFilter: {
    dropdownButton: {
      dropdown:
        "w-[225px] border-animate rounded overflow-y-auto absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
};

const filterHiddenColumns = (column) => column?.show;

const OrderedPartsPage = () => {
  const { selectAll, handleSelectAll, resetSelectAll } = useSelectAll();
  const {
    options: statusFilterOptions,
    handleChange: handleStatusFilterChange,
  } = useMultiSelectStore({ defaultOptions: ORDERED_PART_STATUSES });
  const { columns, setColumns, handleColumnFilterCheck, handleResize } =
    useColumns(DEFAULT_ORDERED_PARTS_COLUMNS);
  const { selectedOrderedParts, setSelectedOrderedParts } = useContext(
    SelectedOrderedPartsContext
  );
  const {
    metaData,
    fetchOrderedParts,
    orderedParts,
    query,
    orderedPartInput,
    setOrderedPartInput,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    isLoading,
    handleChange,
    handleMultiSelectChange,
  } = useContext(OrderedPartsContext);

  const { limit, page, search, sort } = query;
  // Reset selectAll when selectedOrderedParts is empty
  useEffect(() => {
    if (selectedOrderedParts.length === 0) resetSelectAll();
  }, [selectedOrderedParts, resetSelectAll]);
  // Reset selectAll when page changes
  useEffect(() => {
    resetSelectAll();
  }, [page]);

  // Set and fetch statuses to filter by
  useEffect(() => {
    setOrderedPartInput({
      ...orderedPartInput,
      status: statusFilterOptions
        .filter((option) => option.checked)
        .map((option) => option.value),
    });
  }, [statusFilterOptions]);

  // Add/Remove orderedParts from selectedOrderedParts
  const selectAllHandler = () => {
    const updatedSelectedOrderedParts = handleSelectAll(
      selectedOrderedParts,
      orderedParts,
      "_id"
    );
    setSelectedOrderedParts(updatedSelectedOrderedParts);
  };
  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-2 flex flex-col gap-2 overflow-y-hidden"
    >
      <div className="flex flex-row justify-between">
        <QueryContainer
          queryProps={{
            search: {
              handleChange: handleSearch,
            },
            limit: {
              handleChange: handleLimitChange,
              limit: limit,
            },
            columnFilter: {
              handleChange: handleColumnFilterCheck,
              columns: columns,
            },
          }}
          classNames={{
            limit: CLASSNAMES.limit,
            columnFilter: CLASSNAMES.columnFilter,
          }}
        >
          <MultiOptionFilter
            options={statusFilterOptions}
            handleChange={handleStatusFilterChange}
            dropdownButtonProps={{
              label: "Status",
              value:
                statusFilterOptions.filter((option) => option.checked)
                  ?.length || 0,
              classNames: CLASSNAMES.statusFilter.dropdownButton,
            }}
          />
        </QueryContainer>
        <OrderedPartsActionBar />
      </div>

      <div className="p-2">
        <PaginationBar
          classNames={CLASSNAMES.pagination}
          currentPage={page}
          totalPages={metaData.totalPages}
          handleChange={handlePageChange}
        />
      </div>
      <div className="flex flex-row justify-between">
        <OrderedPartsInfoBar />
        <Refresh minimumValue={30} handleEvent={fetchOrderedParts} />
      </div>
      <div className="overflow-x-auto h-full border bg-slate-100 rounded px-1 pb-8">
        {isLoading ? (
          <Spinner classNames={CLASSNAMES.spinner} />
        ) : (
          <Table
            header={
              <tr>
                <th className="max-w-[45px]">
                  <input
                    className="checked:accent-primary w-[45px]"
                    type="checkbox"
                    onChange={selectAllHandler}
                    checked={selectAll}
                  />
                </th>
                {columns?.filter(filterHiddenColumns)?.map((column) => (
                  <ResizableHeader
                    sortProps={{ sort, handleSort }}
                    key={column?.id}
                    column={column}
                    handleResize={handleResize}
                  >
                    {column?.label}
                  </ResizableHeader>
                ))}
              </tr>
            }
            body={orderedParts?.map((orderedPart, index) => (
              <OrderedPartsRow
                handleChange={handleChange}
                handleMultiSelectChange={handleMultiSelectChange}
                key={orderedPart?.id || index}
                columns={columns.filter((column) => column?.show)}
                data={orderedPart}
                classNames={{
                  tableRow: {
                    tr: `h-[50px] ${
                      index % 2 === 0 ? "bg-slate-100" : "bg-gray-300"
                    }`,
                  },
                }}
              />
            ))}
          />
        )}
      </div>
    </div>
  );
};

export default OrderedPartsPage;
